// useAlert.js
import { useContext } from 'react';
import { AlertContext } from './AlertContext';

/**
 * Hook personalizzato per utilizzare gli alert nell'applicazione
 * @returns {{
 *   addAlert: (props: AlertProps) => string,
 *   removeAlert: (id: string) => void,
 *   showConfirm: (props: ConfirmProps) => Promise<boolean>
 * }}
 */
export const useAlert = () => {
  const context = useContext(AlertContext);
  
  if (!context) {
    throw new Error('useAlert deve essere usato all\'interno di AlertProvider');
  }

  // Funzione di utilità per controllare duplicati
  const hasDuplicateAlert = (message, type) => {
    return context.alerts.some(alert => 
      alert.message === message && 
      alert.type === type
    );
  };

  // Creiamo delle utility functions per rendere più facile l'uso comune
  const showSuccess = (message, options = {}) => {
    context.removeAllAlerts();
    return context.addAlert({
      type: 'success',
      message,
      duration: 3000,
      ...options
    })[1];
  };

  const showError = (message, options = {}) => {
    context.removeAllAlerts();
    return context.addAlert({
      type: 'error',
      message,
      duration: 5000,
      ...options
    })[1];
  };

  const showWarning = (message, options = {}) => {
    // Se esiste già un warning con lo stesso messaggio, non mostrarne un altro
    if (hasDuplicateAlert(message, 'warning')) {
      return () => {}; // return empty cleanup function
    }

    return context.addAlert({
      type: 'warning',
      message,
      duration: 4000,
      ...options
    })[1];
  };

  const showInfo = (message, options = {}) => {
    // Per info, rimuoviamo solo alert dello stesso tipo
    context.alerts
      .filter(alert => alert.type === 'info')
      .forEach(alert => context.removeAlert(alert.id));

    return context.addAlert({
      type: 'info',
      message,
      duration: 3000,
      ...options
    })[1];
  };

  return {
    ...context,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};